import { saveAs } from 'file-saver';

interface CalendarEvent {
  title: string;
  description: string;
  startTime: string;
  endTime: string;
  location: string;
}

/**
 * Creates an ICS file and prompts the user to save it.
 * This function generates a calendar event file that can be imported into most calendar applications.
 */
export const addToCalendar = (event: CalendarEvent): void => {
  try {
    // Format the start and end times
    const formatDateTime = (dateTimeStr: string): string => {
      const date = new Date(dateTimeStr);
      // Format: YYYYMMDDTHHMMSSZ (UTC time)
      return date.toISOString().replace(/[-:]/g, "").replace(/\.\d+/g, "");
    };

    // Generate a unique ID for the event
    const generateUID = (): string => {
      return 'UID:' + Math.random().toString(36).substring(2) + '@mediconnect.com';
    };

    // Create the ICS content
    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'CALSCALE:GREGORIAN',
      'PRODID:-//MediConnect//Healthcare Calendar//EN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      generateUID(),
      `SUMMARY:${event.title}`,
      `DESCRIPTION:${event.description.replace(/\n/g, '\\n')}`,
      `LOCATION:${event.location}`,
      `DTSTART:${formatDateTime(event.startTime)}`,
      `DTEND:${formatDateTime(event.endTime)}`,
      'STATUS:CONFIRMED',
      'SEQUENCE:0',
      'BEGIN:VALARM',
      'TRIGGER:-PT30M',
      'ACTION:DISPLAY',
      'DESCRIPTION:Reminder',
      'END:VALARM',
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\n');

    // Create a Blob and save it
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    saveAs(blob, `appointment-${formatDateTime(event.startTime).substring(0, 8)}.ics`);
  } catch (error) {
    console.error('Error generating calendar file:', error);
    alert('There was an error adding this appointment to your calendar. Please try again later.');
  }
};

/**
 * Generates a Google Maps URL for the given location
 */
export const getMapUrl = (location: string): string => {
  if (!location) return '#';
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`;
};

/**
 * Checks if an appointment can be rescheduled based on its status
 */
export const canRescheduleAppointment = (status: string): boolean => {
  return ['scheduled', 'confirmed'].includes(status.toLowerCase());
};

/**
 * Checks if an appointment can be cancelled based on its status
 */
export const canCancelAppointment = (status: string): boolean => {
  return ['scheduled', 'confirmed'].includes(status.toLowerCase());
}; 